import { useMemo, useState } from 'react';
import {
  useAudioUrl,
  useCreateFolder,
  useCreateTag,
  useEngineChoices,
  useFolders,
  useMe,
  useMergeSpeakers,
  useMoveRecordingToFolder,
  useRecording,
  useRecordingTagIds,
  useTags,
  useTranscript,
  useTranscriptVersions,
  useTranscriptWords,
  useTriggerTranscription,
  useUpdateRecordingTags,
  useUpdateSpeaker,
  useUpdateTranscriptSegments,
  useUsersByIds,
  useVocabularies,
} from '../lib/queries';
import { Badge, InProgressStatus, PlaybackSpeedControl, StatusBadge, formatDate, formatDuration } from '../components/ui';
import { TranscriptPlayer } from '../components/TranscriptPlayer';
import { ExportMenu } from '../components/ExportMenu';
import { ShareDialog } from '../components/ShareDialog';
import { inProgressPhase, inProgressSince } from '../hooks/useElapsedLabel';
import { TranscriptionOptions } from '../components/TranscriptionOptions';
import { buildJobSpec, storedTranscriptionLanguage } from '../hooks/useTranscriptionPrefs';
import {
  COMBINED_LANGUAGE_OPTIONS,
  isTranscriptionLanguage,
  selectableVocabularies,
  type TranscriptionLanguage,
} from '../lib/languages';
import { usePlaybackRate } from '../hooks/usePlaybackRate';
import { LiveCaptions } from '../components/LiveCaptions';
import { takePendingCaptions } from '../lib/pendingCaptions';
import { useOnDemandEnabled } from '../lib/ondemandQueries';
import { useOpenTranscription } from '../hooks/useOpenTranscription';
import { OnDemandBanner } from '../components/ondemand/OnDemandBanner';
import { AutoTranscribeSection } from '../components/ondemand/AutoTranscribeSection';
import { problemCodeForRecording } from '@scribe/core/errors';
import { RecordingProblem } from '../components/RecordingProblem';
import { BugReportStatus } from '../components/BugReportStatus';
import { staleRetryEnabled } from '../lib/featureFlags';
import { useFeatureEnabled } from '../lib/features';
import { useEnrollVoicePrint, useMyVoicePrint } from '../lib/voicePrints';
import { isDeferred, type EngineChoice, type Recording, type TranscriptVersion } from '@scribe/core/types';

/** `TranscriptVersion.engine_version` of a transcript made from silence-trimmed audio —
 * mirrors backend `scribe.pipeline.trim.remap.TRIMMED_ENGINE_VERSION` (docs/specs/trim-1.md). */
const TRIMMED_ENGINE_VERSION = 'silence-trimmed';
/** `TranscriptVersion.engine` of an AWS Transcribe version. Only AWS is ever given trimmed
 * audio (backend `scribe.pipeline.trim.gates.engine_reason`), and only AWS versions carry the
 * trim marker; a self-hosted engine's engine_version is its real version. */
const AWS_TRANSCRIBE_ENGINE = 'aws-transcribe';

/** "{engine} · {date}" plus a provenance suffix — the version switcher's option label
 * (TVER-3). Pure helper, covered indirectly via the switcher's rendered option text in
 * RecordingDetail.test.tsx (kept file-local, not exported, so react-refresh/eslint doesn't
 * flag a non-component export from this screen module). */
function versionLabel(v: TranscriptVersion): string {
  const suffix = v.is_authoritative
    ? ' (authoritative)'
    : v.kind === 'imported'
      ? ' (imported)'
      : v.kind === 'derived'
        ? ' (AI-derived)'
        : v.kind === 'comparison'
          ? ' (Whisper comparison)'
          : '';
  return `${v.engine} · ${formatDate(v.created_at)}${suffix}`;
}

/** Whether `recording` has a playable master right now (web-ui-issues #15).
 *
 * Previously gated on `status === 'completed'`, which was overly conservative: the
 * durable master exists as soon as capture finalizes (`finalize_ingest` writes it at
 * stop, for both batch and streaming/live), well before the recording's *transcript*
 * reaches `completed` — so a `queued`/`transcribing` recording already has playable
 * audio and shouldn't wait on the pipeline to show it. `audio_available` is the
 * server-computed signal for this (see `@scribe/core`'s `Recording.audio_available`) —
 * key playback off it instead of `status`. */
function recordingHasAudio(recording: Recording | undefined): boolean {
  if (!recording) return false;
  return recording.audio_available;
}

export function RecordingDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { data: recording, isLoading, error, refetch } = useRecording(id);
  // TVER-3: undefined = the authoritative version. Word-replay (workstream G, REPLAY-2) is
  // now wired below via useTranscriptWords, gated on the selected version's `has_raw_json`
  // — it degrades to today's segment-level highlight for every version that doesn't have
  // fetchable raw JSON (pure-live, derived, imported), and per-segment for any edited or
  // otherwise-mismatched segment within an aligned version.
  const [versionId, setVersionId] = useState<string | undefined>(undefined);
  // VOCAB-10: lets the caller change (not just reuse) the vocabulary before the first
  // Transcribe click on an `uploaded` recording. Defaults to "None"; seeded from the viewed
  // recording's job_spec.vocabulary_name (if any) once it has loaded — see the render-time
  // seeding below, not a useEffect (same reasoning as the versionId reset's comment).
  const [transcribeVocabularyName, setTranscribeVocabularyName] = useState('');
  // §2.7: seeded from THIS recording's own job_spec (below), falling back to the remembered
  // preference. The recording's prior language is the better default for a re-transcribe than a
  // global preference is.
  const [transcribeLanguage, setTranscribeLanguage] = useState<TranscriptionLanguage>(
    storedTranscriptionLanguage,
  );
  // WHISPER-1: seeded from this recording's own job_spec.engine (absent = "aws"), like language.
  const [transcribeEngine, setTranscribeEngine] = useState<EngineChoice>('aws');
  const engines = useEngineChoices();
  // Never submit an engine the server does not currently offer (e.g. a recording made with
  // Whisper after the feature was switched off) — that would be a 400; fall back to AWS.
  const effectiveEngine: EngineChoice = engines.includes(transcribeEngine) ? transcribeEngine : 'aws';
  // App.tsx reuses this component instance across recordings (no `key` on <RecordingDetail>),
  // so state seeded from recording A must never leak into recording B's transcript
  // fetch/edits/export/Transcribe click — the backend's ownership guard turns a mismatched
  // transcript_id into a silent 404, and a leaked vocabulary_name silently transcribes B with
  // A's vocabulary. Reset during render, keyed on the `id` PROP (not `recording?.id`):
  // `recording?.id` only changes once the new recording query resolves, which would leave the
  // whole prop-change -> query-resolution window open, and even a useEffect on `id` runs
  // post-commit — one committed render would still hand the stale versionId/vocabulary to
  // useTranscript/ExportMenu/the Transcribe button and fire the doomed query or mutation. A
  // render-time setState makes React discard this render pass and re-render with the reset
  // state before committing, so no query or child ever observes { new id, old versionId } or
  // { new id, old vocabulary }.
  const [lastId, setLastId] = useState(id);
  // Tracks which recording's job_spec the picker has already been seeded from (see below);
  // `undefined` means "not yet seeded for the current `id`".
  const [vocabSeededForId, setVocabSeededForId] = useState<string | undefined>(undefined);
  // Live captions handed over by RecordScreen when a just-stopped session will be transcribed
  // by a batch pass — shown, dimmed, under "Transcription in progress" instead of a blank gap.
  // Taken exactly once per `id` (the stash clears on read, and the same `lastId` reset that
  // guards versionId/vocabulary re-takes it on an id change), so a later refetch can't
  // resurrect it and one recording's captions can never appear under another's.
  const [carriedCaptions, setCarriedCaptions] = useState(() => takePendingCaptions(id));
  if (lastId !== id) {
    setLastId(id);
    setVersionId(undefined);
    setTranscribeVocabularyName('');
    setTranscribeEngine('aws');
    setVocabSeededForId(undefined);
    setCarriedCaptions(takePendingCaptions(id));
  }
  // Re-seed the picker from the *new* recording's own job_spec.vocabulary_name once its data
  // has actually arrived (recording.id === id, not just "some recording is loaded" — while the
  // query for the new id is still in flight `recording` is either undefined or the previous
  // recording, both of which must be ignored here). Guarded by `vocabSeededForId` so this is a
  // one-time seed per recording, not a sync: later job_spec refetches (e.g. after Transcribe)
  // won't fight the user's own picker edits.
  if (recording && recording.id === id && vocabSeededForId !== id) {
    setVocabSeededForId(id);
    setTranscribeVocabularyName(recording.job_spec?.vocabulary_name ?? '');
    setTranscribeEngine(recording.job_spec?.engine ?? 'aws');
    // A job_spec written before §2.7 carries no `language`; the server read that as auto-detect
    // then and reads it as en-US now, so falling back to the remembered preference (which
    // itself defaults to en-US) matches what a re-transcribe would actually do.
    const storedLanguage = recording.job_spec?.language;
    setTranscribeLanguage(
      isTranscriptionLanguage(storedLanguage) ? storedLanguage : storedTranscriptionLanguage(),
    );
  }
  const { data: versions } = useTranscriptVersions(
    recording?.status === 'completed' ? id : undefined,
  );
  const {
    data: transcript,
    isLoading: transcriptLoading,
    error: transcriptError,
    refetch: refetchTranscript,
  } = useTranscript(recording?.status === 'completed' ? id : undefined, versionId);
  // REPLAY-2: resolve the viewed version's metadata (versionId undefined means
  // authoritative) so the words fetch can gate on its `has_raw_json` — words-less versions
  // (pure-live, derived, imported) never issue the request.
  const viewedVersion = versions?.find((v) =>
    versionId ? v.id === versionId : v.is_authoritative,
  );
  const { data: words } = useTranscriptWords(
    recording?.status === 'completed' ? id : undefined,
    versionId,
    viewedVersion?.has_raw_json === true,
  );
  const hasAudio = recordingHasAudio(recording);
  const { data: audio, isLoading: audioLoading, error: audioError } = useAudioUrl(
    hasAudio ? id : undefined,
  );
  const retranscribe = useTriggerTranscription();
  // One retry/transcribe action for every surface (uploaded banner, failed and stuck problems).
  // Drops the stored job_spec's own vocabulary_name AND language so the controls actually
  // govern the run (selecting "None"/English clears them rather than silently falling back to
  // whatever was picked at record/import time); everything else in the stored spec (mode,
  // auto_summary, max_speakers) is preserved.
  const startRetranscribe = () => {
    if (!recording) return;
    const {
      vocabulary_name: _storedVocab,
      language: _storedLanguage,
      language_options: _storedOptions,
      engine: _storedEngine,
      ...restJobSpec
    } = recording.job_spec ?? {};
    retranscribe.mutate({
      recordingId: id,
      ...restJobSpec,
      ...buildJobSpec(
        transcribeLanguage,
        transcribeVocabularyName,
        COMBINED_LANGUAGE_OPTIONS,
        effectiveEngine,
      ),
    });
  };
  const { data: vocabularies } = useVocabularies();
  // §2.6 step 4: a vocabulary this recording asked for may have been deleted, unpublished, or
  // never registered with AWS. Deleting deliberately leaves `job_spec` alone (it records what was
  // REQUESTED), so this is where the dangling reference has to become visible — otherwise the
  // picker silently falls back to "None" and a re-run quietly loses the bias the original had.
  const requestedVocabularyName = recording?.job_spec?.vocabulary_name ?? null;
  const deadVocabularyName =
    requestedVocabularyName &&
    !selectableVocabularies(vocabularies?.items, transcribeLanguage).some(
      (v) => v.name === requestedVocabularyName,
    )
      ? requestedVocabularyName
      : null;
  const updateSegments = useUpdateTranscriptSegments(id, versionId);
  const updateSpeaker = useUpdateSpeaker(id, versionId);
  const mergeSpeakers = useMergeSpeakers(id, versionId);
  // SHARE-6: speakers linked to a directory user (Speaker.resolved_user_id) need that
  // user's display name to render — resolve the (usually empty) set of linked ids in one
  // batch call. Empty array -> useUsersByIds' query is disabled, so this is zero-cost on
  // recordings with no linked speakers.
  // VOICE-1: a voice-matcher suggestion needs its person's name too.
  const voicePrintsEnabled = useFeatureEnabled('voice_prints');
  const resolvedIds = (transcript?.speakers ?? [])
    .flatMap((s) => [s.resolved_user_id, voicePrintsEnabled ? s.suggested_user_id : null])
    .filter((v): v is string => v != null);
  const { byId: resolvedUsers } = useUsersByIds(resolvedIds);
  const resolvedUserNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const [userId, user] of resolvedUsers) map.set(userId, user.display_name);
    return map;
  }, [resolvedUsers]);
  // ONDEMAND-1: everything on-demand is gated on the server flag — with it off (or its settings
  // query still loading/errored) no other on-demand request fires and the screen is unchanged.
  const onDemandEnabled = useOnDemandEnabled();
  const openTranscription = useOpenTranscription(id, recording, onDemandEnabled);
  const deferred = onDemandEnabled && !!recording && isDeferred(recording);
  const [showShare, setShowShare] = useState(false);
  const [segmentError, setSegmentError] = useState<string | null>(null);
  const [speakerError, setSpeakerError] = useState<string | null>(null);
  const { data: me } = useMe();
  const myVoicePrint = useMyVoicePrint(voicePrintsEnabled);
  const enrollVoicePrint = useEnrollVoicePrint(id);
  const [voiceNote, setVoiceNote] = useState<string | null>(null);

  if (isLoading)
    return (
      <div className="card">
        <p className="muted">Loading recording…</p>
      </div>
    );
  if (error)
    return (
      <div className="card">
        <div className="banner banner-danger">
          <p>{(error as Error).message || 'Failed to load this recording.'}</p>
          <button className="sm" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
        <button className="ghost sm" onClick={onBack}>
          &larr; Back to recordings
        </button>
      </div>
    );
  if (!recording)
    return (
      <div className="card">
        <p className="error">Recording not found.</p>
        <button className="ghost sm" onClick={onBack}>
          &larr; Back to recordings
        </button>
      </div>
    );

  function saveSegmentEdit(segmentId: string, text: string) {
    setSegmentError(null);
    updateSegments.mutate([{ segment_id: segmentId, text }], {
      onError: (err) => setSegmentError(err instanceof Error ? err.message : 'Failed to save edit.'),
    });
  }

  function renameSpeaker(label: string, displayName: string) {
    setSpeakerError(null);
    updateSpeaker.mutate(
      { label, body: { display_name: displayName } },
      {
        onError: (err) =>
          setSpeakerError(err instanceof Error ? err.message : 'Failed to rename speaker.'),
      },
    );
  }

  function resolveSpeaker(label: string, userId: string | null) {
    setSpeakerError(null);
    updateSpeaker.mutate(
      { label, body: { resolved_user_id: userId } },
      { onError: (err) => setSpeakerError(err instanceof Error ? err.message : 'Failed to link speaker.') },
    );
  }

  function claimMyVoice(label: string) {
    const consentVersion = myVoicePrint.data?.consent_version;
    if (!consentVersion) return;
    setVoiceNote(null);
    enrollVoicePrint.mutate(
      { speaker_label: label, consent: true, consent_version: consentVersion },
      {
        onSuccess: () =>
          setVoiceNote('Making your voice print. It’s ready in a few minutes — see Voice print.'),
        onError: (err) =>
          setVoiceNote(err instanceof Error ? err.message : 'Couldn’t make your voice print.'),
      },
    );
  }

  function dismissSuggestion(label: string) {
    setSpeakerError(null);
    updateSpeaker.mutate(
      { label, body: { suggested_user_id: null } },
      { onError: (err) => setSpeakerError(err instanceof Error ? err.message : 'Failed to update speaker.') },
    );
  }

  function mergeSpeakerLabels(from: string[], into: string) {
    setSpeakerError(null);
    mergeSpeakers.mutate(
      { from, into },
      {
        onError: (err) =>
          setSpeakerError(err instanceof Error ? err.message : 'Failed to merge speakers.'),
      },
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <button className="ghost sm" onClick={onBack}>
            &larr; Back
          </button>
          <h1 style={{ marginTop: 12 }}>{recording.title}</h1>
        </div>
        <div className="row">
          {/* TVER-3: minimal version switcher — hidden unless a recording actually has more
              than one transcript version (re-transcription, imports, AI-derived passes). */}
          {(versions?.length ?? 0) > 1 && (
            <label className="field" style={{ margin: 0 }}>
              <select
                aria-label="Transcript version"
                value={versionId ?? ''}
                onChange={(e) => setVersionId(e.target.value || undefined)}
              >
                <option value="">Authoritative</option>
                {versions!.map((v) => (
                  <option key={v.id} value={v.id}>
                    {versionLabel(v)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {/* web-ui-issues #16: `queued`/`transcribing` are genuinely mid-pipeline, so they
              get the honest spinner + live-elapsed treatment instead of a plain badge; every
              other status (including `uploaded`, which is waiting on a manual Transcribe
              click below, not active work) keeps the plain StatusBadge. */}
          {recording.status === 'queued' || recording.status === 'transcribing' ? (
            <InProgressStatus phase={inProgressPhase(recording)} since={inProgressSince(recording)} />
          ) : deferred ? (
            <Badge kind="neutral">Not transcribed</Badge>
          ) : (
            <StatusBadge status={recording.status} />
          )}
          {recording.status === 'completed' && (
            <ExportMenu
              recordingId={recording.id}
              recordingTitle={recording.title}
              transcriptId={versionId}
            />
          )}
          <button className="ghost sm" onClick={() => setShowShare(true)}>
            Share
          </button>
        </div>
      </div>

      <dl className="meta-grid">
        <div className="meta-item">
          <dt>Duration</dt>
          <dd>{formatDuration(recording.duration_sec)}</dd>
        </div>
        {/* TRIM-1: how much silence was cut before this version was transcribed. Absent on
            versions made from the full audio. */}
        {(viewedVersion?.silence_cut_seconds ?? 0) > 0 && (
          <div className="meta-item">
            <dt>Silence cut</dt>
            <dd>{formatDuration(viewedVersion?.silence_cut_seconds)}</dd>
          </div>
        )}
        <div className="meta-item">
          <dt>Language</dt>
          <dd>{recording.language ?? 'auto-detect'}</dd>
        </div>
        <div className="meta-item">
          <dt>Source</dt>
          <dd>{recording.source}</dd>
        </div>
        <div className="meta-item">
          <dt>Created</dt>
          <dd>{formatDate(recording.created_at)}</dd>
        </div>
        <div className="meta-item">
          <dt>PII redaction</dt>
          <dd>{recording.pii_redacted ? 'on' : 'off'}</dd>
        </div>
      </dl>

      {onDemandEnabled && <OnDemandBanner recording={recording} open={openTranscription} />}

      {/* A deferred recording's transcription is started by OnDemandBanner/opening, not by
          this manual banner — showing both would offer two different Transcribe buttons. */}
      {recording.status === 'uploaded' && !deferred && (
        <div className="banner">
          <p>This recording is uploaded but hasn't been transcribed yet.</p>
          <TranscriptionOptions
            idPrefix="retranscribe"
            language={transcribeLanguage}
            onLanguageChange={setTranscribeLanguage}
            vocabularyName={transcribeVocabularyName}
            onVocabularyChange={setTranscribeVocabularyName}
            vocabularies={vocabularies?.items}
            disabled={retranscribe.isPending}
            deadVocabularyName={deadVocabularyName}
            engine={effectiveEngine}
            onEngineChange={setTranscribeEngine}
            engines={engines}
            // Re-transcription is always a batch pass over the finished master, never a live
            // session, so Combined is always available here.
          />
          <button
            className="sm"
            disabled={retranscribe.isPending}
            onClick={startRetranscribe}
          >
            {retranscribe.isPending ? 'Starting…' : 'Transcribe'}
          </button>
          {/* The endpoint can now fail in ways the user must see: 409 when a run is
              already in flight, 503 when the pipeline could not be started. Without this
              the click renders as nothing at all — the same silent failure the trigger fix
              exists to end. `ScribeApiError.message` carries the server's own copy. */}
          {retranscribe.isError && (
            <p className="error">{(retranscribe.error as Error).message}</p>
          )}
        </div>
      )}

      {/* ERRORS-1: the failure is rendered from the shared catalogue — the persisted
          `failure_code` says WHY (legacy rows without one get the generic copy). The retry
          controls stay inside it: a failed or poor transcript is precisely when someone wants
          to try again differently, and a retry must not re-send a vocabulary that may be
          exactly why the run failed. */}
      {recording.status === 'failed' && (
        <RecordingProblem
          code={problemCodeForRecording(recording) ?? 'transcription_job_failed'}
          recordingId={id}
          detail={recording.failure_detail ?? null}
          onPrimaryAction={startRetranscribe}
          primaryPending={retranscribe.isPending}
        >
          <TranscriptionOptions
            idPrefix="retry"
            language={transcribeLanguage}
            onLanguageChange={setTranscribeLanguage}
            vocabularyName={transcribeVocabularyName}
            onVocabularyChange={setTranscribeVocabularyName}
            vocabularies={vocabularies?.items}
            disabled={retranscribe.isPending}
            deadVocabularyName={deadVocabularyName}
            engine={effectiveEngine}
            onEngineChange={setTranscribeEngine}
            engines={engines}
          />
          {retranscribe.isPending && <p className="muted">Retrying…</p>}
          {retranscribe.isError && (
            <p className="error">{(retranscribe.error as Error).message}</p>
          )}
          <BugReportStatus recordingId={id} />
        </RecordingProblem>
      )}

      {/* ERRORS-1 §5 (VITE_FEATURE_STALE_RETRY + backend SCRIBE_STALE_RETRY): a run that has
          been "in progress" longer than any real job or capture can be gets a retry instead of
          an endless spinner. */}
      {staleRetryEnabled() &&
        problemCodeForRecording(recording, { detectStuck: true }) === 'stuck_in_progress' && (
          <RecordingProblem
            code="stuck_in_progress"
            recordingId={id}
            onPrimaryAction={startRetranscribe}
            primaryPending={retranscribe.isPending}
          >
            {retranscribe.isError && (
              <p className="error">{(retranscribe.error as Error).message}</p>
            )}
            <BugReportStatus recordingId={id} />
          </RecordingProblem>
        )}

      {(recording.status === 'queued' || recording.status === 'transcribing') && (
        <>
          <p className="muted">
            Transcription in progress — this page will show the transcript once it completes.
          </p>
          {carriedCaptions && carriedCaptions.length > 0 && (
            <LiveCaptions finalized={carriedCaptions} partial={null} status="done" provisional />
          )}
        </>
      )}

      {recording.status === 'completed' &&
        (transcriptLoading ? (
          <p className="muted">Loading transcript…</p>
        ) : transcriptError ? (
          <div className="banner banner-danger">
            <p>{(transcriptError as Error).message || 'Failed to load the transcript.'}</p>
            <button className="sm" onClick={() => void refetchTranscript()}>
              Retry
            </button>
          </div>
        ) : transcript ? (
          audioLoading ? (
            <p className="muted">Loading audio…</p>
          ) : audioError ? (
            <div className="banner banner-danger">
              <p>{(audioError as Error).message || 'Failed to load audio playback URL.'}</p>
            </div>
          ) : (
            <>
              {/* TRIM-1: this version was transcribed from silence-trimmed audio. Timestamps
                  already refer to the original recording; the action exists for the case a
                  maintainer worried about — content the trimming may have cost. It re-runs the
                  whole recording untrimmed as a NEW version (the trimmed one stays). */}
              {viewedVersion?.engine === AWS_TRANSCRIBE_ENGINE &&
                viewedVersion.engine_version === TRIMMED_ENGINE_VERSION &&
                recording.job_spec?.trim_silence !== false && (
                  <div className="banner">
                    <p>
                      Long silences were skipped to transcribe this recording. If anything seems
                      to be missing, transcribe the full recording again without skipping.
                    </p>
                    <button
                      className="sm"
                      disabled={retranscribe.isPending}
                      onClick={() =>
                        retranscribe.mutate({
                          recordingId: id,
                          ...(recording.job_spec ?? {}),
                          trim_silence: false,
                        })
                      }
                    >
                      {retranscribe.isPending ? 'Starting…' : 'Re-transcribe without trimming'}
                    </button>
                    {retranscribe.isError && (
                      <p className="error">{(retranscribe.error as Error).message}</p>
                    )}
                  </div>
                )}
              {segmentError && <p className="error">{segmentError}</p>}
              {speakerError && <p className="error">{speakerError}</p>}
              {voiceNote && <p role="status" className="muted">{voiceNote}</p>}
              <TranscriptPlayer
                audioUrl={audio?.url ?? ''}
                transcript={transcript}
                onEditSegment={saveSegmentEdit}
                savingSegment={updateSegments.isPending}
                onRenameSpeaker={renameSpeaker}
                onMergeSpeakers={mergeSpeakerLabels}
                onResolveSpeaker={resolveSpeaker}
                resolvedUserNames={resolvedUserNames}
                savingSpeaker={updateSpeaker.isPending || mergeSpeakers.isPending}
                words={words}
                voicePrint={
                  voicePrintsEnabled && me?.id && myVoicePrint.data
                    ? {
                        myUserId: me.id,
                        onUseMyVoice: claimMyVoice,
                        onDismissSuggestion: dismissSuggestion,
                        busy: enrollVoicePrint.isPending,
                      }
                    : undefined
                }
              />
            </>
          )
        ) : (
          <>
            <AudioSection
              hasAudio={hasAudio}
              audioLoading={audioLoading}
              audioError={audioError}
              audioUrl={audio?.url}
            />
            <p className="muted">No transcript available yet.</p>
          </>
        ))}

      {/* Recordings not yet `completed` don't get the transcript-linked player above (no
          transcript to sync against yet), but per web-ui-issues #15 they still get their
          own standalone player as soon as `audio_available` is true — independent of the
          transcript's own pending/failed state, which the status banners/messages above
          already surface. */}
      {recording.status !== 'completed' && (
        <AudioSection
          hasAudio={hasAudio}
          audioLoading={audioLoading}
          audioError={audioError}
          audioUrl={audio?.url}
        />
      )}

      {onDemandEnabled && (
        <AutoTranscribeSection recording={recording} />
      )}

      <div className="row-secondary">
        <h3 className="label" style={{ marginBottom: 8 }}>
          Folder &amp; tags
        </h3>
        <OrganizeRecording recording={recording} />
      </div>

      {showShare && (
        <ShareDialog
          resourceType="recording"
          resourceId={recording.id}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}

/**
 * A standalone (transcript-independent) audio player for recordings that don't yet have
 * the transcript-linked `TranscriptPlayer` rendered (any non-`completed` status, or a
 * `completed` recording whose transcript isn't ready) — INGEST-3, docs/specs/phase1-3.md
 * section 3. Shows a plain `<audio>` element when a playback URL is available, and a
 * clear "not available yet" state (rather than a broken player) when the recording
 * genuinely has no master yet — e.g. a streaming/live capture still in progress.
 */
function AudioSection({
  hasAudio,
  audioLoading,
  audioError,
  audioUrl,
}: {
  hasAudio: boolean;
  audioLoading: boolean;
  audioError: unknown;
  audioUrl: string | undefined;
}) {
  // Defense in depth against a stale/optimistic `audio_available`: if the <audio>
  // element itself fails to load the presigned URL (the master object doesn't actually
  // exist yet), fall back to the same honest "not available yet" note instead of a dead
  // player. Keyed by URL so a later, fresh presign retries rather than staying failed.
  const [failedAudioUrl, setFailedAudioUrl] = useState<string | null>(null);
  const { rate: playbackRate, setRate: setPlaybackRate, bindAudio } = usePlaybackRate();
  if (!hasAudio) {
    return <p className="muted">Audio not available yet.</p>;
  }
  if (audioLoading) {
    return <p className="muted">Loading audio…</p>;
  }
  if (audioError) {
    return (
      <div className="banner banner-danger">
        <p>{(audioError as Error).message || 'Failed to load audio playback URL.'}</p>
      </div>
    );
  }
  if (!audioUrl || failedAudioUrl === audioUrl) {
    return <p className="muted">Audio not available yet.</p>;
  }
  return (
    <div className="audio-bar">
      <audio
        ref={bindAudio}
        controls
        src={audioUrl}
        style={{ width: '100%' }}
        onError={() => setFailedAudioUrl(audioUrl)}
      />
      <div className="audio-bar-controls">
        <PlaybackSpeedControl rate={playbackRate} onChange={setPlaybackRate} />
      </div>
    </div>
  );
}

/**
 * Move a recording to a folder and edit its tags.
 *
 * Folder move (task WEB-C3) has a real `PATCH /recordings/{id}` contract, and tag editing
 * (task WEB-C2) has a real `GET`/`PUT /recordings/{id}/tags` contract, so both work
 * identically in mock and real-backend mode.
 */
function OrganizeRecording({ recording }: { recording: Recording }) {
  return (
    <>
      <FolderSection recording={recording} />
      <TagsSection recording={recording} />
    </>
  );
}

/** Sentinel `<option>` value that opens the inline "New folder…" input (never a real folder
 * id, so it can't collide with one). */
const NEW_FOLDER_OPTION = '__new_folder__';

/**
 * web-ui-issues #12: folder assignment (`PATCH /recordings/{id}`, `useMoveRecordingToFolder`)
 * plus inline folder creation. "New folder…" is the last option in the existing select — no
 * separate combobox/modal — and picking it swaps the select for a small create form backed by
 * the same `useCreateFolder` mutation the Folders management screen uses. On success the new
 * folder is applied to this recording immediately (`moveToFolder.mutate`), so create-then-assign
 * is a single flow instead of "create, then go find it in the dropdown."
 */
function FolderSection({ recording }: { recording: Recording }) {
  const { data: folders } = useFolders();
  const moveToFolder = useMoveRecordingToFolder(recording.id);
  const createFolder = useCreateFolder();
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  function onSelectChange(value: string) {
    if (value === NEW_FOLDER_OPTION) {
      setNewFolderName('');
      setCreatingFolder(true);
      return;
    }
    moveToFolder.mutate(value || null);
  }

  function submitNewFolder(e: React.FormEvent) {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;
    createFolder.mutate(
      { name },
      {
        onSuccess: (folder) => {
          setCreatingFolder(false);
          setNewFolderName('');
          moveToFolder.mutate(folder.id);
        },
      },
    );
  }

  return (
    <>
      <div className="field">
        <span className="label">Folder</span>
        {creatingFolder ? (
          <form className="row" onSubmit={submitNewFolder}>
            <input
              aria-label="New folder name"
              placeholder="New folder name"
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setCreatingFolder(false);
              }}
            />
            <button className="sm" disabled={!newFolderName.trim() || createFolder.isPending}>
              {createFolder.isPending ? 'Creating…' : 'Create & assign'}
            </button>
            <button
              type="button"
              className="ghost sm"
              onClick={() => setCreatingFolder(false)}
              disabled={createFolder.isPending}
            >
              Cancel
            </button>
          </form>
        ) : (
          <select
            aria-label="Recording folder"
            value={recording.folder_id ?? ''}
            disabled={moveToFolder.isPending}
            onChange={(e) => onSelectChange(e.target.value)}
          >
            <option value="">No folder</option>
            {folders?.items.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
            <option value={NEW_FOLDER_OPTION}>New folder…</option>
          </select>
        )}
      </div>
      {createFolder.isError && <p className="error">{(createFolder.error as Error).message}</p>}
      {moveToFolder.isError && (
        <p className="error">{(moveToFolder.error as Error).message}</p>
      )}
    </>
  );
}

/**
 * web-ui-issues #12: tag assignment (`PUT /recordings/{id}/tags`, `useUpdateRecordingTags`)
 * plus inline tag creation. The picker is a filter/search input over the existing checkbox
 * list (`useTags`) — typing narrows the list to matching tags, and when nothing matches the
 * typed text exactly (case-insensitive), a "Create "<text>"" affordance appears, backed by the
 * same `useCreateTag` mutation the Tags management screen uses. On success the new tag is
 * applied to this recording immediately (folded into the existing selected-ids set and saved
 * via `updateTags.mutate`), so create-then-assign is a single flow.
 */
function TagsSection({ recording }: { recording: Recording }) {
  const { data: tags } = useTags();
  const { data: tagIds } = useRecordingTagIds(recording.id);
  const updateTags = useUpdateRecordingTags(recording.id);
  const createTag = useCreateTag();
  const [query, setQuery] = useState('');

  const selectedTagIds = new Set(tagIds ?? []);
  const allTags = tags?.items ?? [];
  const trimmedQuery = query.trim();
  const visibleTags = trimmedQuery
    ? allTags.filter((t) => t.name.toLowerCase().includes(trimmedQuery.toLowerCase()))
    : allTags;
  const hasExactMatch = allTags.some(
    (t) => t.name.toLowerCase() === trimmedQuery.toLowerCase(),
  );
  const canCreate = trimmedQuery.length > 0 && !hasExactMatch;

  function toggleTag(tagId: string) {
    const next = new Set(selectedTagIds);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    updateTags.mutate(Array.from(next));
  }

  function createAndAssignTag() {
    const name = trimmedQuery;
    if (!name) return;
    createTag.mutate(
      { name },
      {
        onSuccess: (tag) => {
          updateTags.mutate(Array.from(new Set([...selectedTagIds, tag.id])));
          setQuery('');
        },
      },
    );
  }

  return (
    <>
      <div className="field">
        <span className="label">Tags</span>
        <input
          type="search"
          aria-label="Find or create a tag"
          placeholder="Find or create a tag"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {allTags.length === 0 && !trimmedQuery ? (
          <p className="muted">No tags yet — type a name above to create one.</p>
        ) : visibleTags.length > 0 ? (
          <div className="tags" style={{ marginTop: 8 }}>
            {visibleTags.map((t) => (
              <label key={t.id} className="merge-checkbox">
                <input
                  type="checkbox"
                  checked={selectedTagIds.has(t.id)}
                  disabled={updateTags.isPending}
                  onChange={() => toggleTag(t.id)}
                />
                {t.name}
              </label>
            ))}
          </div>
        ) : null}
        {canCreate && (
          <button
            type="button"
            className="ghost sm"
            style={{ marginTop: 8 }}
            disabled={createTag.isPending}
            onClick={createAndAssignTag}
          >
            {createTag.isPending ? 'Creating…' : `Create "${trimmedQuery}"`}
          </button>
        )}
      </div>
      {createTag.isError && <p className="error">{(createTag.error as Error).message}</p>}
      {updateTags.isError && <p className="error">{(updateTags.error as Error).message}</p>}
    </>
  );
}
